import { Edit, Eye, Home, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../../services/api';
import TenantModal from '../Tenants/TenantModal';

const RMCTenants = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [modalMode, setModalMode] = useState('view'); // RMC는 'view'만 (임차인 추가는 계약으로만)
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const queryClient = useQueryClient();

  // 사용자 정보 가져오기
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
      }
    }
  );

  // RMC 사용자 여부 확인
  const isRMCUser = userInfo?.role === 'rental_management_company' || 
                    userInfo?.role === 'rmc_admin' || 
                    userInfo?.role === 'rmc_staff';

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => {
      // RMC 사용자는 RMC 전용 API 사용
      if (isRMCUser) {
        return api.get('/rmc/property-owners');
      }
      return api.get('/property-owners');
    },
    {
      enabled: isRMCUser,
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );

  // 건물 목록 가져오기 (RMC 사용자는 임대사업자별)
  const { data: buildings } = useQuery(
    ['buildings', selectedPropertyOwner, userInfo?.role],
    () => {
      if (isRMCUser) {
        // RMC 사용자는 선택된 임대사업자의 건물
        const url = selectedPropertyOwner
          ? `/buildings?property_owner_id=${selectedPropertyOwner}`
          : '/buildings';
        return api.get(url);
      } else {
        // 임대사업자 사용자는 자신의 건물
        return api.get('/buildings');
      }
    },
    {
      enabled: !!userInfo && (!isRMCUser || !!selectedPropertyOwner),
      select: (data) => {
        // API 응답이 배열인지 확인하고, 아니면 빈 배열 반환
        if (Array.isArray(data)) {
          return data;
        }
        // 에러 응답이나 다른 구조인 경우 빈 배열 반환
        console.warn('buildings API 응답이 배열이 아닙니다:', data);
        return [];
      }
    }
  );

  // 임차인 목록 가져오기
  const { data: tenantsData, isLoading, error } = useQuery(
    ['tenants', selectedPropertyOwner, selectedBuilding],
    () => {
      const params = new URLSearchParams();
      if (selectedPropertyOwner) {
        params.append('property_owner_id', selectedPropertyOwner);
      }
      if (selectedBuilding) {
        params.append('building_id', selectedBuilding);
      }
      const url = params.toString() ? `/tenants?${params.toString()}` : '/tenants';
      return api.get(url);
    },
    {
      enabled: !!userInfo && !!selectedBuilding,
      onSuccess: (data) => {
        console.log('🔍 임차인 목록 조회 성공:', data);
      },
      onError: (error) => {
        console.error('임차인 목록 조회 실패:', error);
      }
    }
  );

  // tenants 데이터 안전하게 처리
  const tenants = Array.isArray(tenantsData) ? tenantsData : 
                  Array.isArray(tenantsData?.data) ? tenantsData.data : 
                  Array.isArray(tenantsData?.tenants) ? tenantsData.tenants : [];

  useEffect(() => {
    if (!isRMCUser || selectedPropertyOwner || !Array.isArray(propertyOwners) || propertyOwners.length === 0) {
      return;
    }

    setSelectedPropertyOwner(String(propertyOwners[0].id));
  }, [isRMCUser, propertyOwners, selectedPropertyOwner]);

  useEffect(() => {
    if (!Array.isArray(buildings) || buildings.length === 0) {
      return;
    }

    const hasSelectedBuilding = buildings.some((building) => String(building.id) === String(selectedBuilding));
    if (!hasSelectedBuilding) {
      setSelectedBuilding(String(buildings[0].id));
    }
  }, [buildings, selectedBuilding]);

  // 임대사업자 변경 핸들러
  const handlePropertyOwnerChange = (propertyOwnerId) => {
    setSelectedPropertyOwner(propertyOwnerId);
    setSelectedBuilding(''); // 건물 선택 초기화
  };

  // 임차인 삭제
  const deleteTenantMutation = useMutation(
    (tenantId) => api.delete(`/tenants/${tenantId}`),
    {
      onSuccess: () => {
        toast.success('임차인이 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries('tenants');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '임차인 삭제에 실패했습니다.');
      }
    }
  );

  const handleDeleteTenant = (tenantId) => {
    if (window.confirm('정말로 이 임차인을 삭제하시겠습니까?')) {
      deleteTenantMutation.mutate(tenantId);
    }
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTenant(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries('tenants');
    handleCloseModal();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">오류가 발생했습니다: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">임차인 관리 (RMC 전용)</h1>
          <p className="mt-1 text-sm text-gray-500">
            관리하는 건물의 임차인 현황 및 관리 - RMC 전용 화면
          </p>
        </div>
        {/* 임차인추가 버튼 제거 (계약으로만 추가되므로) */}
      </div>

      {/* 필터 섹션 */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 임대사업자 필터 (RMC 사용자용) */}
          {isRMCUser && (
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">
                임대사업자:
              </label>
              <select
                value={selectedPropertyOwner}
                onChange={(e) => handlePropertyOwnerChange(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">전체 임대사업자</option>
                {propertyOwners?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.company_name || owner.name} ({owner.buildings_count}개 건물)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 건물 필터 (모든 사용자용) */}
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">
              건물:
            </label>
            <select
              value={selectedBuilding}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              disabled={userInfo?.role === 'rental_management_company' && !selectedPropertyOwner}
            >
              <option value="">전체 건물</option>
              {buildings && Array.isArray(buildings) && buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 임차인 목록 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            임차인 목록 ({tenants.length}명)
          </h3>
        </div>
        
        {tenants.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">임차인이 없습니다.</div>
            <div className="text-gray-400 text-sm">
              계약을 통해 임차인을 등록해주세요.
            </div>
          </div>
        ) : (
          <>
          <div className="divide-y divide-gray-200 md:hidden">
            {tenants.map((tenant) => (
              <div key={tenant.id} className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {tenant.name || '-'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {tenant.phone || '-'}
                    </p>
                  </div>
                  <span className={`inline-flex shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${
                    tenant.contract_status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {tenant.contract_status === 'active' ? '활성' : '비활성'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">이메일</span>
                    <span className="truncate text-right font-medium text-gray-900">
                      {tenant.email || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-500">건물/세대</span>
                    <span className="text-right font-medium text-gray-900">
                      {tenant.building_name || '-'} / {tenant.unit_number || '-'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <button
                    onClick={() => handleViewTenant(tenant)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-blue-200 px-2 text-sm font-medium text-blue-700"
                  >
                    <Eye className="h-4 w-4" />
                    보기
                  </button>
                  <button
                    onClick={() => handleEditTenant(tenant)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-indigo-200 px-2 text-sm font-medium text-indigo-700"
                  >
                    <Edit className="h-4 w-4" />
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteTenant(tenant.id)}
                    className="inline-flex min-h-[40px] items-center justify-center gap-1 rounded-md border border-red-200 px-2 text-sm font-medium text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이메일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    건물/세대
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계약 상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {tenant.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {tenant.building_name} / {tenant.unit_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        tenant.contract_status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tenant.contract_status === 'active' ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewTenant(tenant)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTenant(tenant.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* 임차인 모달 */}
      {showModal && (
        <TenantModal
          isOpen={showModal}
          tenant={selectedTenant}
          mode={modalMode}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default RMCTenants;
